import { defaultUnitsForLocale, type Units } from '@geosport/shared';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import type { MapStyleKind } from '../map/style';
import { isRecord, readJson, STORAGE_KEYS, writeJson } from './storage';

export type MotionPref = 'system' | 'reduce' | 'full';

interface Settings {
  units: Units;
  motion: MotionPref;
  mapStyle: MapStyleKind;
}

interface SettingsState extends Settings {
  setUnits: (units: Units) => void;
  setMotion: (motion: MotionPref) => void;
  setMapStyle: (mapStyle: MapStyleKind) => void;
}

const isSettings = (v: unknown): v is Partial<Settings> => isRecord(v);

function load(): Settings {
  const stored = readJson(STORAGE_KEYS.settings, isSettings) ?? {};
  const units: Units =
    stored.units === 'km' || stored.units === 'mi'
      ? stored.units
      : defaultUnitsForLocale(typeof navigator === 'undefined' ? undefined : navigator.language);
  const motion: MotionPref =
    stored.motion === 'reduce' || stored.motion === 'full' ? stored.motion : 'system';
  const mapStyle: MapStyleKind = stored.mapStyle === 'dark' ? 'dark' : 'satellite';
  return { units, motion, mapStyle };
}

const save = (s: Settings): void => writeJson(STORAGE_KEYS.settings, s);

export const useSettings = create<SettingsState>()((set, get) => ({
  ...load(),
  setUnits: (units) => {
    set({ units });
    save({ units, motion: get().motion, mapStyle: get().mapStyle });
  },
  setMotion: (motion) => {
    set({ motion });
    save({ units: get().units, motion, mapStyle: get().mapStyle });
  },
  setMapStyle: (mapStyle) => {
    set({ mapStyle });
    save({ units: get().units, motion: get().motion, mapStyle });
  },
}));

function systemReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** True when animations should be skipped (setting or OS preference, PRD §10). */
export function useReducedMotion(): boolean {
  const motion = useSettings((s) => s.motion);
  const [system, setSystem] = useState(systemReducedMotion);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = (): void => setSystem(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  if (motion === 'reduce') return true;
  if (motion === 'full') return false;
  return system;
}
